import { Suspense, lazy, type ReactElement } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Navigate } from 'react-router-dom';
import { useGetMyProfileQuery } from '../../../services/ProfileService';
import type { ProfileView } from '../../../services/ProfileService';
import { hasConfigurationAccess, hasDenialsAccess } from '../../profile';
import { DENIALS_QUEUE_PATH } from './routeConfig';

const DenialsQueuePage = lazy(() =>
  import('../features/queue').then((m) => ({ default: m.DenialsQueuePage })),
);

const DenialsWorklistPage = lazy(() =>
  import('../features/queue').then((m) => ({ default: m.DenialsWorklistPage })),
);

const DenialItemDetailPage = lazy(() =>
  import('../features/detail').then((m) => ({ default: m.DenialItemDetailPage })),
);

const DenialsRunReportPage = lazy(() =>
  import('../features/reports').then((m) => ({ default: m.DenialsRunReportPage })),
);

const DenialsReportPage = lazy(() =>
  import('../features/reports').then((m) => ({ default: m.DenialsReportPage })),
);

const DenialsAdminPage = lazy(() =>
  import('../features/admin').then((m) => ({ default: m.DenialsAdminPage })),
);

const DenialRulesPage = lazy(() =>
  import('../features/rules').then((m) => ({ default: m.DenialRulesPage })),
);

const NeedsInfoPage = lazy(() =>
  import('../features/needsInfo').then((m) => ({ default: m.NeedsInfoPage })),
);

/**
 * The body both guards below share: mount `children` ONLY once the profile has resolved AND `admits`
 * accepts it, so an unauthorized user's page shell and data hooks never mount. A neutral loading
 * state while the profile is unresolved avoids a redirect flash; direct navigation is denied
 * otherwise. Mirrors `modules/claims/routes/RequireCamEdit.tsx`.
 *
 * It is private and takes the test as a prop because the module now has two guards that differ in
 * nothing else — two copies of this body is the Code Duplication the gate reports.
 */
function RequireProfile({
  admits,
  children,
}: {
  admits: (profile?: ProfileView) => boolean;
  children: ReactElement;
}): ReactElement {
  const { data: profile, isLoading } = useGetMyProfileQuery();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress data-testid="denials-auth-loading" />
      </Box>
    );
  }

  if (!admits(profile))
    return <Navigate to="/" replace />;

  return children;
}

/** Route-level authorization guard for the denials-claim-gated routes (FR-013). */
export function RequireDenialsAccess({ children }: { children: ReactElement }): ReactElement {
  return <RequireProfile admits={hasDenialsAccess}>{children}</RequireProfile>;
}

/**
 * Route-level authorization guard for the denials admin page (US5, SC-010).
 *
 * Gates on the configuration role pair — "Administrator" or "Rev Cycle Administrator" — and on
 * NOTHING else, because that is exactly what the two routes behind the page carry
 * (`StartFullHistoryEndpoint.ConfigurationAdminRoles`, which `ReadFullHistoryEndpoint` shares). The
 * platform has no configuration-admin claim; `hasConfigurationAccess` is the client's test of that
 * same pair. Matching it exactly is what keeps the card from showing a control that answers 403, and
 * from hiding from a caller the backend would allow. The Denials claim is NOT part of the test: an
 * Administrator without it is a caller both routes admit.
 */
export function RequireConfigurationAccess({ children }: { children: ReactElement }): ReactElement {
  return <RequireProfile admits={hasConfigurationAccess}>{children}</RequireProfile>;
}

/**
 * Lazy route element for the denials worklist. Authorization is enforced by `RequireDenialsAccess`,
 * which mounts the page — and its queue queries — only for a user with the Denials claim, so no
 * content renders and no read fires before the guard evaluates (FR-013).
 */
export function DenialsQueueRouteElement() {
  return (
    <RequireDenialsAccess>
      <Suspense fallback={<CircularProgress data-testid="denials-suspense-fallback" />}>
        <DenialsQueuePage />
      </Suspense>
    </RequireDenialsAccess>
  );
}

/** Lazy route element for one queue's worklist, behind the same guard as the dashboard (FR-013). */
export function DenialsWorklistRouteElement() {
  return (
    <RequireDenialsAccess>
      <Suspense fallback={<CircularProgress data-testid="denials-suspense-fallback" />}>
        <DenialsWorklistPage />
      </Suspense>
    </RequireDenialsAccess>
  );
}

/**
 * Lazy route element for one denial item in full (US2). Same guard as the queue: the page and its
 * item read mount only for a user with the Denials claim, so a typed item URL discloses nothing
 * before the guard evaluates (FR-013).
 */
export function DenialItemDetailRouteElement() {
  return (
    <RequireDenialsAccess>
      <Suspense fallback={<CircularProgress data-testid="denials-suspense-fallback" />}>
        <DenialItemDetailPage />
      </Suspense>
    </RequireDenialsAccess>
  );
}

/**
 * Lazy route element for one day's run report (US3). Same guard as the queue and the item: the
 * counts and the four splits of a day's pass mount only for a user with the Denials claim (FR-013).
 */
export function DenialsRunReportRouteElement() {
  return (
    <RequireDenialsAccess>
      <Suspense fallback={<CircularProgress data-testid="denials-suspense-fallback" />}>
        <DenialsRunReportPage />
      </Suspense>
    </RequireDenialsAccess>
  );
}

/**
 * Lazy route element for one day's created items (US3). Same guard again: the rows name the account,
 * the payer and the source 835 of every item, so they mount only for a user with the Denials claim
 * (FR-013).
 */
export function DenialsReportRouteElement() {
  return (
    <RequireDenialsAccess>
      <Suspense fallback={<CircularProgress data-testid="denials-suspense-fallback" />}>
        <DenialsReportPage />
      </Suspense>
    </RequireDenialsAccess>
  );
}

/**
 * Lazy route element for the denials admin card (US5). The one denials route NOT behind
 * `RequireDenialsAccess`: its page reads and writes the full-history request, which the backend gates
 * on the configuration role pair alone, so `RequireConfigurationAccess` is the guard that matches it.
 */
export function DenialsAdminRouteElement() {
  return (
    <RequireConfigurationAccess>
      <Suspense fallback={<CircularProgress data-testid="denials-suspense-fallback" />}>
        <DenialsAdminPage />
      </Suspense>
    </RequireConfigurationAccess>
  );
}

/**
 * Lazy route element for the denial rules page (US1), behind `RequireDenialsAccess`: the rules read,
 * save and Carelon upload now all gate on the `Denials` claim server-side. **Keep the guard** —
 * `DenialsRulesRouteElement`'s own test asserts the wrap, because a test around the guard alone
 * cannot see whether the element `App.tsx` registers is wrapped at all.
 */
export function DenialsRulesRouteElement() {
  return (
    <RequireDenialsAccess>
      <Suspense fallback={<CircularProgress data-testid="denials-suspense-fallback" />}>
        <DenialRulesPage />
      </Suspense>
    </RequireDenialsAccess>
  );
}

/**
 * Lazy route element for the Needs Info screen. Same guard as the queue: the rows name the claim,
 * the account and the facility of every incomplete manual request.
 */
export function DenialsNeedsInfoRouteElement() {
  return (
    <RequireDenialsAccess>
      <Suspense fallback={<CircularProgress data-testid="denials-suspense-fallback" />}>
        <NeedsInfoPage />
      </Suspense>
    </RequireDenialsAccess>
  );
}

/**
 * Route element for the two retired Denied Claims paths (US4). Both land on the queue list.
 *
 * The old detail path carried a remit id and a claim index, which cannot name one denial item: an
 * item collects the denials of an account from every remit that denied it, so one remit plus one
 * index has no single item to resolve to. The queue list is the only correct destination.
 */
export function LegacyDenialsRedirect() {
  return <Navigate to={DENIALS_QUEUE_PATH} replace />;
}
